<template>
  <div class="viq-pdf-export">
    <el-button
      :loading="exporting"
      size="small"
      type="primary"
      @click="exportPdf"
    >
      <svg v-if="!exporting" width="14" height="14" viewBox="0 0 14 14" fill="none" style="margin-right: 6px; vertical-align: middle;">
        <path d="M12 9v3H2V9M7 1v8M4 5l3 3 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
      Export PDF
    </el-button>
  </div>
</template>

<script>
export default {
  name: "ViqPdfExport",
  props: {
    title: {
      type: String,
      default: "VIQ Report",
    },
    content: {
      type: String,
      default: "",
    },
    sections: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      exporting: false,
    };
  },
  methods: {
    async exportPdf() {
      this.exporting = true;

      try {
        const JsPDF = (await import("jspdf")).default;
        const doc = new JsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 20;
        const maxWidth = pageWidth - margin * 2;
        let y = margin;

        // Title
        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.text(this.title, margin, y);
        y += 12;

        // Date
        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(107, 114, 128);
        doc.text(
          `Generated ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`,
          margin,
          y
        );
        y += 8;

        // Divider
        doc.setDrawColor(229, 231, 235);
        doc.line(margin, y, pageWidth - margin, y);
        y += 8;

        // Reset text color
        doc.setTextColor(26, 26, 46);

        // Content
        if (this.sections && this.sections.length > 0) {
          this.sections.forEach((section) => {
            // Check page break
            if (y > 270) {
              doc.addPage();
              y = margin;
            }

            // Section title
            doc.setFontSize(14);
            doc.setFont("helvetica", "bold");
            doc.text(section.title || "", margin, y);
            y += 8;

            // Section content
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            const plainText = this.stripHtml(section.content || "");
            const lines = doc.splitTextToSize(plainText, maxWidth);

            lines.forEach((line) => {
              if (y > 280) {
                doc.addPage();
                y = margin;
              }
              doc.text(line, margin, y);
              y += 5;
            });

            y += 6;
          });
        } else if (this.content) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          const plainText = this.stripHtml(this.content);
          const lines = doc.splitTextToSize(plainText, maxWidth);

          lines.forEach((line) => {
            if (y > 280) {
              doc.addPage();
              y = margin;
            }
            doc.text(line, margin, y);
            y += 5;
          });
        }

        // Footer
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(107, 114, 128);
          doc.text(
            `VIXIO VIQ - Page ${i} of ${totalPages}`,
            margin,
            doc.internal.pageSize.getHeight() - 10
          );
        }

        // Save
        const filename = this.title
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
        doc.save(`${filename || "viq-report"}.pdf`);

        this.$notify({
          title: "PDF Exported",
          message: "Report has been downloaded.",
          type: "success",
          duration: 3000,
        });
      } catch (err) {
        this.$notify({
          title: "Export Failed",
          message: "Could not generate PDF. Please try again.",
          type: "error",
          duration: 3000,
        });
      } finally {
        this.exporting = false;
      }
    },
    stripHtml(html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },
  },
};
</script>

<style scoped lang="scss">
.viq-pdf-export {
  display: inline-block;
}
</style>
